import { View, Text, styled, useTheme } from 'tamagui'
import { useState } from 'react'
import { TIER_CONFIGS, serverBilling } from '@/mocks'
import { FUEL_LEVELS } from '@/mocks/billing'
import type { Server } from '@/types'

const Banner = styled(View, {
  marginHorizontal: 8,
  marginBottom: 8,
  backgroundColor: '$warningLighter',
  borderWidth: 1,
  borderColor: '$warning',
  borderRadius: '$borderRadiusLg',
  padding: 10,
  gap: 6,
})

const ContributeButton = styled(View, {
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$warning',
  cursor: 'pointer',
  hoverStyle: { opacity: 0.9 },
  // @ts-expect-error -- web transition
  style: { transition: 'opacity 100ms ease' },
})

interface FuelSupportBannerProps {
  server: Server
}

export function FuelSupportBanner({ server }: FuelSupportBannerProps) {
  const theme = useTheme()
  const [contributed, setContributed] = useState(false)

  const config = TIER_CONFIGS[server.tier]
  if (!config.fuelEnabled) return null

  // Check if current user (u1) is already a contributor
  const billing = serverBilling[server.id]
  const isContributor = billing?.fuelContributors.some(fc => fc.userId === 'u1')
  if (isContributor) return null

  if (contributed) {
    return (
      <Banner borderColor="$accent" backgroundColor="$accentLighter">
        <Text fontFamily="$fontBody" fontSize={12} fontWeight="600" color="$accent" textAlign="center">
          Thank you for supporting {server.name}!
        </Text>
      </Banner>
    )
  }

  const contributorCount = billing?.fuelContributors.length || 0
  const currentLevel = FUEL_LEVELS.filter(l => contributorCount >= l.goal).pop()
  const nextLevel = FUEL_LEVELS.find(l => contributorCount < l.goal) || FUEL_LEVELS[FUEL_LEVELS.length - 1]
  const progress = Math.min(100, (contributorCount / nextLevel.goal) * 100)

  return (
    <Banner>
      <Text fontFamily="$fontBody" fontSize={12} fontWeight="600" color="$warning">
        Support {server.name}
      </Text>
      <View flexDirection="row" alignItems="center" justifyContent="space-between">
        <Text fontFamily="$fontMono" fontSize={10} color="$sidebarMuted">
          Level {currentLevel ? currentLevel.level : 0}
        </Text>
        <Text fontFamily="$fontMono" fontSize={10} color="$sidebarMuted">
          {contributorCount}/{nextLevel.goal} contributors
        </Text>
      </View>
      <View height={6} backgroundColor="$sidebar" borderRadius="$borderRadiusSm" overflow="hidden">
        <View
          height={6}
          backgroundColor="$warning"
          borderRadius="$borderRadiusSm"
          // @ts-expect-error -- web width
          style={{ width: `${progress}%`, transition: 'width 300ms ease' }}
        />
      </View>
      {nextLevel && (
        <Text fontFamily="$fontMono" fontSize={10} color="$color8">
          Level {nextLevel.level}: {nextLevel.perks}
        </Text>
      )}
      <Text fontFamily="$fontBody" fontSize={11} color="$sidebarMuted">
        Contribute ${config.fuelPrice}/mo to help keep this server running.
      </Text>
      <ContributeButton
        onPress={() => setContributed(true)}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: theme.shadowButton?.val }}
      >
        <Text fontFamily="$fontBody" fontWeight="600" fontSize={12} color="$surface">
          Contribute
        </Text>
      </ContributeButton>
    </Banner>
  )
}
